import { RecordBatchStreamReader } from 'apache-arrow';

import { TimeRangeUtil } from '../utils/time';

import Request from './request';

import {
  AnomalousTagsPayload,
  AnomalousTagsResult,
  AnomalyRange,
  ApiListResult,
  ApiResult,
  ApiTimeServicePayload,
  AutoLabeledEventsTag,
  CRFolderTags,
  CRTagData,
  DeleteHiddenTagsResult,
  Ensemble,
  EventSearchResult,
  FetchHiddenTagsResult,
  HiddenTag,
  HideTagsResult,
  LimitData,
  Mode,
  PredictedDataPayload,
  PredictedTagsMinMaxResult,
  ResampleTagsMinMaxResult,
  RollingAverageTagsMinMaxResult,
  RollingAverageWithAnomaliesResult,
  TagMinMaxRollingAveragePayload,
  TagRollingAverageWithAnomaliesPayload,
  // SearchTagsMinMaxResult,
  TagResampleMinMaxPayload,
  // TagSearchMinMaxPayload,
  TagStateResult,
  TimeSearchPayload,
  TimeSearchResult,
  TimeSeries,
} from '@controlrooms/models';

const SERVICE_ROOT = '/tag-explorer/api/v1';
const SERVICE_ROOT_V2 = '/tag-explorer/api/v2';

function buildStreamingPayload(payload: ApiTimeServicePayload) {
  const { preset, ...rest } = payload;
  let _payload = rest;
  if (preset) {
    // Update the start / end time based on the relative time selection
    _payload = {
      ..._payload,
      ...TimeRangeUtil.ofDuration(preset),
    };
  }
  return _payload;
}

export const hideTag = (hideTagData: { folder: number; tag: string }): Promise<HideTagsResult> => {
  const _payload = {
    folder: hideTagData.folder,
    tag_name: hideTagData.tag,
    visibility: 'private',
  };
  return Request.post(`user-customize/accounts-customize/hidden_tags`, _payload, {
    timeout: 15000,
  }).then((r) => new HideTagsResult(r));
};

export const unHideTag = (tagPayload: HiddenTag): Promise<DeleteHiddenTagsResult> => {
  const { tag, folder } = tagPayload;
  return Request.delete(`user-customize/accounts-customize/hidden_tags/${tag}?folder=${folder}`, {
    timeout: 15000,
  }).then((r) => new DeleteHiddenTagsResult(r));
};

export const fetchAllHiddenTags = async (): Promise<FetchHiddenTagsResult> => {
  return Request.get(`user-customize/accounts-customize/hidden_tags`, {
    timeout: 15000,
  }).then((r) => new FetchHiddenTagsResult(r));
};

// export const searchTagsMinMax = (
//   payload: TagSearchMinMaxPayload,
// ): Promise<SearchTagsMinMaxResult> => {
//   const _payload = buildStreamingPayload(payload);
//   return Request.post(`${SERVICE_ROOT}/tags/search/resampled-minmax`, _payload, {
//     timeout: 15000,
//   }).then((r) => new SearchTagsMinMaxResult(r));
// };

export const resampledTagsMinMax = (
  payload: TagResampleMinMaxPayload,
  ensembleFamilyId: string,
): Promise<ResampleTagsMinMaxResult> => {
  const _payload = buildStreamingPayload(payload);
  return Request.post(
    `${SERVICE_ROOT}/tags/resampled_minmax_tags${
      ensembleFamilyId ? `?ensemble=${ensembleFamilyId}` : ''
    }`,
    _payload,
    {
      timeout: 15000,
    },
  ).then((r) => new ResampleTagsMinMaxResult(r));
};

export const limitTagsMinMax = async (
  payload: TagResampleMinMaxPayload,
  ensembleFamilyId: string,
): Promise<any> => {
  const _payload = buildStreamingPayload(payload);
  return Request.post(
    `/tag-explorer/api/v3/tags/limits${ensembleFamilyId ? `?ensemble=${ensembleFamilyId}` : ''}`,
    _payload,
    {
      timeout: 15000,
    },
  ).then(async (r) => new LimitData(r));
};

export const resampledTagsMinMaxV2 = async (
  payload: TagResampleMinMaxPayload,
  ensembleFamilyId: string,
  onBatch: (data: CRTagData<TimeSeries, Mode, AnomalyRange>) => void,
): Promise<any> => {
  const _payload = buildStreamingPayload(payload);
  return Request.post(
    `/tag-explorer/api/v3/tags/resampled_minmax_tags${
      ensembleFamilyId ? `?ensemble=${ensembleFamilyId}` : ''
    }`,
    _payload,
    {
      timeout: 15000,
      responseType: 'arraybuffer',
    },
  ).then(async (r) => {
    const reader = RecordBatchStreamReader.from(r.result);

    // Create a clean tagData object - no direct mutation
    const resultTagData: CRTagData<TimeSeries, Mode, AnomalyRange> = {
      tag: payload.tag_names?.[0] || '',
      timeseries: [],
      anomalies: [],
      descr: payload.tag_names?.[0] || '',
      modes: [],
      highs: [],
      highHighs: [],
      lows: [],
      lowLows: [],
      ranges: {
        timeseries: { min: null, max: null },
        frequent: { min: null, max: null },
        highs: { min: null, max: null },
        highHighs: { min: null, max: null },
        lows: { min: null, max: null },
        lowLows: { min: null, max: null },
      },
    };

    let globalMin: any = null;
    let globalMax: any = null;
    let batchCount = 0;
    let lastBatchProcessed = false;

    for await (const batch of reader) {
      const batchArray = batch.toArray();

      // Process batch data
      const processedData = batchArray.map((row: any) => {
        const minVal = row['min_value'];
        const maxVal = row['max_value'];
        if (typeof minVal === 'number' && (globalMin === null || minVal < globalMin))
          globalMin = minVal;
        if (typeof maxVal === 'number' && (globalMax === null || maxVal > globalMax))
          globalMax = maxVal;
        return {
          time: new Date(row['time']).getTime(),
          value: row['value'],
          min: minVal,
          max: maxVal,
          timestamp: '',
          severity: row['severity'],
        };
      });

      resultTagData.timeseries.push(...processedData);
      if (resultTagData.ranges && resultTagData.ranges.timeseries) {
        resultTagData.ranges.timeseries.min = globalMin;
        resultTagData.ranges.timeseries.max = globalMax;
      }

      // Throttle onBatch calls - only call every 5 batches or on first batch
      batchCount++;
      if (batchCount % 5 === 0 || batchCount === 1) {
        await new Promise((resolve) => requestAnimationFrame(resolve));
        onBatch({ ...resultTagData });
        lastBatchProcessed = true;
      } else {
        lastBatchProcessed = false;
      }
    }

    // Final onBatch call - only if the last batch wasn't already processed
    if (batchCount > 0 && !lastBatchProcessed) {
      onBatch({ ...resultTagData });
    }
    return resultTagData;
  });
};

export const rollingAverageTagsMinMax = (
  payload: TagMinMaxRollingAveragePayload,
): Promise<RollingAverageTagsMinMaxResult> => {
  return Request.post(`${SERVICE_ROOT}/tags/rolling_average`, payload, {
    timeout: 15000,
  }).then((r) => new RollingAverageTagsMinMaxResult(r));
};

// export const resampledTagsMinMax = (
//   payload: TagResampleMinMaxPayload,
//   ensembleFamilyId: string,
// ): Promise<ResampleTagsMinMaxResult> => {
//   const _payload = buildStreamingPayload(payload);
//   return Request.post(
//     `${SERVICE_ROOT}/tags/resampled_minmax_tags${
//       ensembleFamilyId ? `?ensemble=${ensembleFamilyId}` : ''
//     }`,
//     _payload,
//     {
//       timeout: 15000,
//     },
//   ).then((r) => new ResampleTagsMinMaxResult(r));
// };

export const rollingAverageWithAnomalies = (
  payload: TagRollingAverageWithAnomaliesPayload,
  ensembleFamilyId?: string,
): Promise<RollingAverageWithAnomaliesResult> => {
  return Request.post(
    `${SERVICE_ROOT}/tags/rolling_average_with_anomalies${
      ensembleFamilyId ? `?ensemble=${ensembleFamilyId}` : ''
    }`,
    payload,
    {
      timeout: 15000,
    },
  ).then((r) => new RollingAverageWithAnomaliesResult(r));
};

export const predictedTagsMinMaxData = (
  payload: PredictedDataPayload,
): Promise<PredictedTagsMinMaxResult> => {
  const _payload = buildStreamingPayload(payload);
  return Request.post(`${SERVICE_ROOT_V2}/tags/predicted_data`, _payload, {
    timeout: 15000,
  }).then((r) => new PredictedTagsMinMaxResult(r));
};

export const getAutoLabeledEvents = (
  payload: TagResampleMinMaxPayload,
): Promise<ApiResult<AutoLabeledEventsTag[]>> => {
  const _payload = buildStreamingPayload(payload);
  return Request.post(`${SERVICE_ROOT_V2}/label_events/tags`, _payload, {
    timeout: 15000,
  });
};

export const resampledTagsMinMaxString = (
  payload: TagResampleMinMaxPayload,
  ensembleFamilyId: string,
): Promise<ResampleTagsMinMaxResult> => {
  const _payload = buildStreamingPayload(payload);
  return Request.post(
    `${SERVICE_ROOT_V2}/tags/resampled_minmax_string_tags${
      ensembleFamilyId ? `?ensemble=${ensembleFamilyId}` : ''
    }`,
    _payload,
    {
      timeout: 15000,
    },
  ).then((r) => new ResampleTagsMinMaxResult(r));
};

export const resampledTagsMinMaxDiscrete = (
  payload: TagResampleMinMaxPayload,
  ensembleFamilyId: string,
): Promise<ResampleTagsMinMaxResult> => {
  const _payload = buildStreamingPayload(payload);
  return Request.post(
    `${SERVICE_ROOT_V2}/tags/resampled_minmax_discrete_tags${
      ensembleFamilyId ? `?ensemble=${ensembleFamilyId}` : ''
    }`,
    _payload,
    {
      timeout: 15000,
    },
  ).then((r) => new ResampleTagsMinMaxResult(r));
};

export const getTagStates = (payload: {
  tag_name: string;
  infra_node_id: number;
}): Promise<TagStateResult> => {
  return Request.post(`${SERVICE_ROOT_V2}/tags/tag_states`, payload).then(
    (r) => new TagStateResult(r),
  );
};

export const anomalousTags = (
  payload: AnomalousTagsPayload,
  ensembleFamilyId: string,
): Promise<ApiListResult<CRFolderTags>> => {
  const _payload = buildStreamingPayload(payload);
  return Request.post(
    `${SERVICE_ROOT}/anomalous_tags_in_order${
      ensembleFamilyId ? `?ensemble=${ensembleFamilyId}` : ''
    }`,
    _payload,
    { timeout: 15000 },
  ).then((r) => new AnomalousTagsResult(r));
};

export const timeSearch = (
  payload: TimeSearchPayload,
): Promise<ApiListResult<TimeSearchResult>> => {
  const SERVICE_ROOT = '/ml/api/v2/tagSearch';
  const _payload = {
    tag_name: payload.tag_name,
    tag_start_time: payload.startTime,
    tag_end_time: payload.endTime,
    search_start_time: payload.searchStartTime,
    search_end_time: payload.searchEndTime,
  };
  return Request.post(`${SERVICE_ROOT}`, _payload, { timeout: 120000 }).then(
    (r) => new EventSearchResult(r),
  );
};

export const getEnsembleModels = (): Promise<ApiResult<{ family_list: Ensemble[] }>> =>
  Request.get(`${SERVICE_ROOT_V2}/ensemble_family`);
